import { Skeleton } from '#/shared/ui/skeleton'

import {
  useCampaignStrategyChips,
  useCampaignStrategySeed,
} from '../model/use-campaign-strategy-chips'
import type { CampaignStrategySeed } from '../model/use-campaign-strategy-chips'

function ChipRowSkeleton() {
  return (
    <div className="flex flex-wrap items-center gap-1.5" aria-busy="true">
      <Skeleton className="h-5.5 w-28 rounded-full" />
      <Skeleton className="h-5.5 w-20 rounded-full" />
    </div>
  )
}

/** Mounted only once the saved rule is in: the filter builder seeds its state
 *  at mount, so the definition has to be there before the first render. */
function ChipRow({ seed }: { seed: CampaignStrategySeed }) {
  const { chips, isLoading } = useCampaignStrategyChips(seed)
  if (isLoading) return <ChipRowSkeleton />
  // A manual campaign has no rule, and an unnarrowed one has nothing to say
  // that the note below the chips does not already say.
  if (chips.length === 0) return null
  return (
    <ul className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => (
        <li
          key={chip.key}
          className="rounded-full border border-gray-200 bg-white px-2.5 py-0.75 text-xs text-gray-500"
        >
          {chip.label}
        </li>
      ))}
    </ul>
  )
}

/**
 * The rule the campaign enrols by, on the Strategy card. The chips the
 * overview sends carry the trigger event only, so the filters come from the
 * campaign's own read.
 */
export function CampaignStrategyChips({ campaignId }: { campaignId: string }) {
  const { seed, isLoading } = useCampaignStrategySeed(campaignId)
  if (isLoading) return <ChipRowSkeleton />
  if (seed == null) return null
  // Keyed on the saved version: a save through the Edit dialog re-seeds the
  // builder's mount-time state, which is where the chips are read from.
  return <ChipRow key={seed.updatedAt} seed={seed} />
}
